import { useAvailableSocialExportSources } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { useFragment } from '@app/modules/fragments/screens/view/hooks/useFragment';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import {
  NoPlatformsAlert,
  NoSourcesAlert,
  UploadsPanoramaLayout,
  publishMessages,
} from '@app/modules/social-export/publish';
import { FragmentSocialUploadItemDto } from '@app/modules/fragments/fragment.types';
import { QueryView } from '@mi-front/ui';
import { SocialExportUploadItem } from './SocialExportUploadItem';
import { useFragmentSocialUploadsQuery } from './hooks/useFragmentSocialUploadsQuery';

interface SocialExportPanoramaProps {
  /** When provided, use this instead of running the query (dropdown is rendered by parent). */
  readonly uploadsByPlatform?: Map<string, FragmentSocialUploadItemDto[]>;
}

/**
 * Social export panorama: one BFF call for all fragment social uploads (raw media + video editor
 * edits). Shows a flat list of upload cards. When uploadsByPlatform is not provided, runs the
 * query and also shows the "Publicar en" dropdown; when provided by parent, dropdown is in the parent header.
 * In-progress uploads are polled individually via getUploadById.
 */
export function SocialExportPanorama({
  uploadsByPlatform: externalUploadsByPlatform,
}: SocialExportPanoramaProps = {}) {
  const { fragment } = useFragment();
  const { hasAvailableSources } = useAvailableSocialExportSources({
    fragmentId: fragment.id,
    stationId: fragment.stationId,
  });
  const query = useFragmentSocialUploadsQuery(
    externalUploadsByPlatform === undefined ? fragment.id : undefined,
  );
  const uploadsByPlatform = externalUploadsByPlatform ?? query.data;
  const { platforms: availablePlatforms, areConnectionsResolved } =
    useAvailableSocialExportPlatformsForStation(fragment.stationId);
  const allUploads = Array.from(uploadsByPlatform?.values() ?? []).flat();

  const content = (
    <UploadsPanoramaLayout
      uploadCount={allUploads.length}
      hasAvailableSources={hasAvailableSources}
      areConnectionsResolved={areConnectionsResolved}
      hasConnectedPlatforms={availablePlatforms.length > 0}
      notPublishedMessage={publishMessages.panoramaNotPublishedFragment}
      noSourcesAlert={
        <NoSourcesAlert
          description={publishMessages.panoramaNoSourcesFragment}
          linkTo={`/fragments/${fragment.id}/edits`}
        />
      }
      noPlatformsAlert={<NoPlatformsAlert />}
      uploadList={
        <>
          {allUploads.map((upload) => (
            <SocialExportUploadItem key={upload.id} upload={upload} />
          ))}
        </>
      }
    />
  );

  if (externalUploadsByPlatform !== undefined) {
    return content;
  }

  return (
    <QueryView query={query} inline>
      {() => content}
    </QueryView>
  );
}
